import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, Grid, Card, LinearProgress, CircularProgress, Chip } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Controls from '../../components/controls/Controls';
import * as MyCollections from "../../services/MyCollections";
import { urlLogActivity } from '../../config';

export default function ActivityHistory({ token, darkTheme }) {
    const [activityHistory, setActivityHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const gridRef = useRef();
    const [selectedRange, setSelectedRange] = useState("10");

    const columnDefs = [
        {
            headerName: 'Aktywność',
            wrapText: true,
            field: 'action',
            sortable: true,
            filter: true,
            minWidth: 420,
            autoHeight: true,
            cellStyle: {
                textAlign: 'left',
                lineHeight: '1.5',
                paddingTop: '9px',
                paddingBottom: '5px',
            },
            cellRenderer: (params) => {
                if (!params.value) return null;
                const lines = params.value.split('\n');
                return lines.map((line, index) => (
                    <span key={index}>
                        {line}
                        <br />
                    </span>
                ));
            },
        },
        { headerName: 'Użytkownik', field: 'userName', sortable: true, filter: true, minWidth: 150 },
        { headerName: 'Czas zdarzenia', field: 'timeStamp', sortable: true, filter: true, minWidth: 150, headerClass: 'no-separator' }
    ];

    const defaultColDef = {
        resizable: true,
        editable: false,
        filter: true,
        floatingFilter: true,
        autoHeight: true,
        flex: 1
    };

    const clearFilters = useCallback(() => {
        const api = gridRef.current?.api;
        const columnApi = gridRef.current?.columnApi;

        if (api) {
            api.setFilterModel(null);

            if (api.applyColumnState) {
                api.applyColumnState({
                    defaultState: { sort: null },
                });
            } else if (columnApi && columnApi.applyColumnState) {
                columnApi.applyColumnState({
                    defaultState: { sort: null },
                });
            } else if (api.setSortModel) {
                api.setSortModel(null);
            }
        }
    }, []);

    const onGridReady = useCallback((params) => {
        gridRef.current = params;
        if (loading) {
            params.api.showLoadingOverlay();
        }
    }, [loading]);

    const fetchActivity = useCallback((range = "10") => {
        setLoading(true);
        if (gridRef.current?.api) {
            gridRef.current.api.showLoadingOverlay();
        }
        fetch(urlLogActivity + '/' + range, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(resp => {
                if (!resp.ok) {
                    throw new Error(`HTTP error! status: ${resp.status}`);
                }
                return resp.json();
            })
            .then(data => {
                const rows = data || [];
                setActivityHistory(rows);
                setLoading(false);
                if (gridRef.current?.api) {
                    if (rows.length === 0) {
                        gridRef.current.api.showNoRowsOverlay();
                    } else {
                        gridRef.current.api.hideOverlay();
                    }
                }
            })
            .catch(error => {
                console.error('Błąd pobierania historii aktywności:', error);
                setLoading(false);
                if (gridRef.current?.api) {
                    gridRef.current.api.showNoRowsOverlay();
                }
            });
    }, [token]);

    const handleRangeChange = (event) => {
        setSelectedRange(event.target.value);
    };

    useEffect(() => {
        fetchActivity(selectedRange);
    }, [fetchActivity, selectedRange]);

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>
                    Historia Aktywności
                </Typography>
                <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    Dziennik audytu rejestrujący operacje wykonywane przez użytkowników w systemie Pelikan.
                </Typography>
            </Box>

            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2.5,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#FFFFFF',
                    p: 2.5
                }}
            >
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Controls.Select
                            name="range"
                            label="Zakres raportu (dni)"
                            value={selectedRange}
                            onChange={handleRangeChange}
                            options={MyCollections.getActivityLogRangeCollection()}
                            size="small"
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Controls.PremiumButton
                            variant="glass"
                            onClick={clearFilters}
                            label="Wyczyść filtry"
                            icon="fas fa-filter"
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6.5} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.5 }}>
                                <CircularProgress size={18} thickness={5} sx={{ color: '#3B82F6' }} />
                                <Typography variant="body2" sx={{ color: darkTheme ? '#93C5FD' : '#2563EB', fontWeight: 600, fontSize: '0.85rem' }}>
                                    Trwa pobieranie danych audytu...
                                </Typography>
                            </Box>
                        ) : (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${activityHistory.length} zdarzeń (ostatnie ${selectedRange} dni)`}
                                sx={{
                                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.15)' : '#CBD5E1',
                                    color: darkTheme ? '#94A3B8' : '#64748B',
                                    fontSize: '0.8rem',
                                    fontWeight: 500
                                }}
                            />
                        )}
                    </Grid>
                </Grid>

                {loading && (
                    <LinearProgress
                        sx={{
                            borderRadius: 1,
                            mb: 1.5,
                            height: 4,
                            backgroundColor: darkTheme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #3B82F6 0%, #10B981 100%)'
                            }
                        }}
                    />
                )}

                <div
                    className={darkTheme ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
                    style={{ height: 600, width: '100%', borderRadius: 8, overflow: 'hidden' }}
                >
                    <AgGridReact
                        key={darkTheme ? 'grid-dark' : 'grid-light'}
                        rowData={activityHistory.slice().reverse()}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        ref={gridRef}
                        onGridReady={onGridReady}
                        suppressBrowserResizeObserver={true}
                        animateRows={true}
                        rowSelection={false}
                        suppressSizeToFit={true}
                        headerHeight={45}
                        localeText={MyCollections.localeTextPL}
                        popupParent={document.body}
                        overlayLoadingTemplate={'<div class="ag-overlay-loading-center" style="display: flex; align-items: center; gap: 10px; font-weight: 600;"><i class="fas fa-spinner fa-spin" style="color: #3B82F6; font-size: 1.2rem;"></i><span>Trwa pobieranie danych audytu...</span></div>'}
                        overlayNoRowsTemplate={loading ? '<div class="ag-overlay-loading-center" style="display: flex; align-items: center; gap: 10px; font-weight: 600;"><i class="fas fa-spinner fa-spin" style="color: #3B82F6; font-size: 1.2rem;"></i><span>Trwa pobieranie danych audytu...</span></div>' : `<div class="ag-overlay-loading-center" style="font-weight: 500;">Brak zdarzeń w wybranym okresie (${selectedRange} dni)</div>`}
                    />
                </div>
            </Card>
        </Box>
    );
}
